/**
 * Single source of truth for the auth zustand-persist contract.
 *
 * Three out-of-band readers (`api/api-helpers`, `api/geometry-api`,
 * `utils/clientLogger`) parse this localStorage key directly — they cannot
 * import the `useAuth` store without a circular import. This module centralises
 * the persist-envelope knowledge (`{ state, version }`), version-checks it, and
 * derives the auth header, so those readers no longer each re-implement it.
 *
 * IMPORTANT: `AUTH_STORAGE_KEY` must stay byte-identical with `useAuth`'s
 * persist `name`. Both derive it from `ns('auth')` (#896) — one `APP_NS`
 * resolved once at module scope in `lib/storage` — so they cannot diverge.
 * Changing the logical key orphans every existing user's session.
 */

import { ns, storageGet, sessionGet, sessionSet, sessionRemove } from '../lib/storage';

/** zustand-persist key for the auth store. Must match `useAuth`'s persist `name`. */
export const AUTH_STORAGE_KEY = ns('auth');

/**
 * Raw per-tab sessionStorage key holding the opaque impersonation id (#701).
 * SINGLE source of truth for the `X-Impersonation-Id` header — read out-of-band
 * here (the header injectors cannot import `useImpersonation` without a cycle).
 * Value is the bare id string (not a zustand envelope).
 */
export const IMPERSONATION_ID_KEY = ns('impersonation-id');

/** sessionStorage key for the `useImpersonation` zustand-persist envelope (#701). */
export const IMPERSONATION_STORE_KEY = ns('impersonation');

/** Current persist schema version for the auth store. Bump on a breaking shape change. */
export const AUTH_PERSIST_VERSION = 1;

/** #662 DEV/TEST-ONLY selected role ceiling. Prod server ignores the header. */
export type DevRole = 'admin' | 'facility' | 'user';

/** The subset of persisted auth state the out-of-band readers need. */
export interface PersistedAuthState {
  authMethod?: 'basic' | 'oauth' | null;
  /** Legacy (pre-#353): OAuth tokens are no longer persisted — auth rides the httpOnly cookie. */
  accessToken?: string | null;
  basicAuthHeader?: string | null;
  /** #662 DEV/TEST-ONLY: selected role ceiling, sent as X-Dev-Role on every backend call so the session stays clamped across reload. Null/absent ⇒ real role. */
  devRole?: DevRole | null;
  /** Persisted user identity (subset). Read out-of-band to scope per-user stores (#485). */
  user?: { username?: string } | null;
}

/**
 * Read and version-check the persisted auth state from localStorage.
 *
 * Returns the persisted state iff the envelope version is the current version,
 * the legacy `0`, or absent (`undefined`) — the v0→v1 migration is identity, so
 * legacy envelopes (and test fixtures that omit `version`) stay readable.
 * Returns `null` for an unknown higher version (stale-parse safety), corrupt
 * JSON, or a missing key.
 */
export function readPersistedAuthState(): PersistedAuthState | null {
  const raw = storageGet(AUTH_STORAGE_KEY);
  if (!raw) return null;

  try {
    const envelope = JSON.parse(raw) as { state?: PersistedAuthState; version?: number };
    const { version } = envelope;
    const readable =
      version === AUTH_PERSIST_VERSION || version === 0 || version === undefined;
    if (!readable) return null;
    return envelope.state ?? null;
  } catch {
    return null;
  }
}

/**
 * The persisted authenticated username, or `null` when absent/corrupt. Read
 * out-of-band (not via `useAuth`, which would be a circular import) so per-user
 * stores can resolve their localStorage key synchronously at hydration (#485).
 */
export function readPersistedUsername(): string | null {
  return readPersistedAuthState()?.user?.username ?? null;
}

/**
 * The persisted `user` object (whatever subset was persisted), or `null`. Read
 * out-of-band so the auth persist `partialize` can pin the admin identity when an
 * impersonation tab must not clobber the shared `everguide-auth.user` (#701).
 */
export function readPersistedAuthUser(): PersistedAuthState['user'] | null {
  return readPersistedAuthState()?.user ?? null;
}

/**
 * The opaque impersonation id for THIS tab (#701), or `null` when the tab is not
 * impersonating. Read from the raw per-tab sessionStorage key.
 */
export function readImpersonationId(): string | null {
  return sessionGet(IMPERSONATION_ID_KEY) || null;
}

/** Write the per-tab impersonation id (#701). */
export function writeImpersonationId(id: string): void {
  sessionSet(IMPERSONATION_ID_KEY, id);
}

/** Clear the per-tab impersonation id (#701). */
export function clearImpersonationId(): void {
  sessionRemove(IMPERSONATION_ID_KEY);
}

/**
 * The impersonation target username for THIS tab (#701), read from the
 * `useImpersonation` sessionStorage persist envelope. `null` when not
 * impersonating / absent / corrupt. Read out-of-band (no `useImpersonation`
 * import → no cycle).
 */
export function readImpersonationEffectiveUser(): string | null {
  try {
    const raw = sessionGet(IMPERSONATION_STORE_KEY);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as { state?: { active?: boolean; effectiveUser?: string | null } };
    if (envelope.state?.active && envelope.state.effectiveUser) {
      return envelope.state.effectiveUser;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * The per-tab EFFECTIVE active username (#701/#703): the impersonation target
 * when this tab is impersonating, else the persisted admin/user identity. Drives
 * per-user store scoping (`usePreferences`, `useBuildingsState`) so the
 * impersonation tab scopes to the target while the admin's other tabs stay admin.
 */
export function readActiveUsername(): string | null {
  return readImpersonationEffectiveUser() ?? readPersistedUsername();
}

/**
 * Derive the HTTP `Authorization` header value from persisted auth state.
 *
 * Since #353 (BFF cookie sessions) only Basic auth produces a header: OAuth
 * sessions authenticate via the httpOnly session cookie, which the browser
 * attaches to same-origin requests automatically — callers simply omit the
 * `Authorization` header when this returns null.
 */
export function authHeaderFromPersistedState(state: PersistedAuthState | null): string | null {
  if (!state) return null;
  if (state.authMethod === 'basic' && state.basicAuthHeader) {
    return state.basicAuthHeader;
  }
  return null;
}

/**
 * Is there any persisted authentication at all? True for Basic (header-based)
 * AND for OAuth (httpOnly-cookie-based, #353). Callers that used to treat
 * "no Authorization header" as "not authenticated" must use this instead —
 * a cookie session has no header but IS authenticated.
 */
export function isPersistedAuthenticated(state: PersistedAuthState | null): boolean {
  return state?.authMethod === 'basic' || state?.authMethod === 'oauth';
}

/**
 * Authorization headers for a fetch to the backend: `{ Authorization }` for
 * Basic auth, `{}` for cookie sessions (the browser attaches the httpOnly
 * cookie to same-origin requests automatically).
 */
export function authHeadersFromPersistedState(state: PersistedAuthState | null): Record<string, string> {
  const header = authHeaderFromPersistedState(state);
  const headers: Record<string, string> = header ? { Authorization: header } : {};
  // #662 DEV/TEST-ONLY: carry the role ceiling on every backend call so a
  // header-less request never triggers a server-side session re-create at the
  // real role (the reuse guard keys on the ceiling). Prod server ignores it.
  if (state?.devRole) headers['X-Dev-Role'] = state.devRole;
  // #701 per-tab impersonation: carry the opaque id so the backend applies the
  // request-scoped derived-session swap ONLY to this (impersonation) tab. Absent
  // on admin/normal tabs → no swap → the request runs as the real identity.
  const impersonationId = readImpersonationId();
  if (impersonationId) headers['X-Impersonation-Id'] = impersonationId;
  return headers;
}
